// src/crm/pages/MsaPoDashboard.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import BASE_URL from '../../url';
import CommercialDocumentModal from '../components/CommercialDocumentModal';
import TemplateModal from '../components/TemplateModal';
import { 
  LuFileText, 
  LuSearch, 
  LuEye, 
  LuPencil,
  LuTrash2,
  LuPlus, 
  LuArrowLeft, 
  LuFilePlus, 
  LuUpload, 
  LuFileCheck, 
  LuChevronDown, 
  LuLayers,
  LuExternalLink,
  LuDownload,
  LuBan,
  LuBellRing,
  LuPlay,
  LuCheck,
  LuX,
  LuAlertTriangle,
  LuMail,
  LuCalendar
} from 'react-icons/lu';
import './MsaPo.css';

const ENTITY_MAP = {
  '6': {
    id: 6,
    code: '5785467',
    name: 'Cognifyar',
    fullName: 'Cognifyar Technologies',
    themeClass: 'card-cognifyar',
    accentColor: '#6366f1'
  },
  '5': {
    id: 5,
    code: '25642740',
    name: 'Prophecy Offshore',
    fullName: 'Prophecy Offshore Services',
    themeClass: 'card-offshore',
    accentColor: '#0284c7'
  },
  '4': {
    id: 4,
    code: '24386543',
    name: 'Prophecy Consulting INC',
    fullName: 'Prophecy Consulting INC',
    themeClass: 'card-prophecy',
    accentColor: '#0284c7'
  }
};

const MsaPoDashboard = () => {
  const { entityId } = useParams();
  const navigate = useNavigate();

  // Dynamic entities from Accounting / Companies API
  const [entitiesList, setEntitiesList] = useState([]);

  useEffect(() => {
    const fetchLiveEntities = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${BASE_URL}/api/companies`, { headers });
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        if (Array.isArray(list) && list.length > 0) {
          setEntitiesList(list);
        }
      } catch (err) {
        console.warn('Could not load live companies for dashboard:', err.message);
      }
    };
    fetchLiveEntities();
  }, []);

  // Active entity details resolved dynamically
  const currentEntity = useMemo(() => {
    const fallbackMeta = ENTITY_MAP[String(entityId)] || {
      id: Number(entityId) || 4,
      code: '25642730',
      name: 'Prophecy Consulting Inc',
      fullName: 'Prophecy Consulting Inc',
      accentColor: '#0284c7'
    };

    const liveMatch = entitiesList.find(e => String(e.Id || e.id) === String(entityId));
    if (liveMatch) {
      const matchName = liveMatch.Name || liveMatch.name || fallbackMeta.name;
      const matchCode = liveMatch.ClientId || liveMatch.clientId || fallbackMeta.code;
      return {
        ...fallbackMeta,
        id: Number(liveMatch.Id || liveMatch.id),
        code: matchCode,
        name: matchName,
        fullName: matchName
      };
    }
    return fallbackMeta;
  }, [entityId, entitiesList]);

  // 3 Primary Tabs: 'MSA', 'PO', 'TEMPLATES'
  const [activeTab, setActiveTab] = useState('MSA');

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [sideFilter, setSideFilter] = useState('ALL');

  // Modal State for CommercialDocumentModal
  const [commModal, setCommModal] = useState({
    open: false,
    mode: 'VIEW_AND_SIGN',
    documentId: null,
    documentTypeCode: 'MSA'
  });

  // Modal State for TemplateModal (View & Edit Template)
  const [templateModal, setTemplateModal] = useState({
    open: false,
    template: null,
    mode: 'VIEW' // 'VIEW' | 'EDIT'
  });

  const handleOpenTemplateModal = (tmpl, mode = 'VIEW') => {
    setTemplateModal({
      open: true,
      template: tmpl,
      mode
    });
  };

  const handleDeleteTemplate = async (tmpl) => {
    const result = await Swal.fire({
      title: 'Delete Template?',
      html: `<div style="text-align: left; font-size: 13px; color: #334155; line-height: 1.5;">
        Are you sure you want to delete template <b>"${tmpl.TemplateName || 'this template'}"</b>?
        <br/><br/>
        <span style="color: #64748b;">This template will no longer be available in the library.</span>
      </div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });
    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        await axios.delete(`${BASE_URL}/api/crm/commercial-documents/templates/${tmpl.CommercialTemplateId}`, { headers });
      } catch (dErr) {
        if (dErr.response?.status === 404) {
          await axios.put(`${BASE_URL}/api/crm/commercial-documents/templates/${tmpl.CommercialTemplateId}`, { isDelete: true }, { headers });
        } else {
          throw dErr;
        }
      }
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Template Deleted',
        timer: 1500,
        showConfirmButton: false
      });
      fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: err.response?.data?.message || err.message || 'Failed to delete template.'
      });
    }
  };

  // SOW Terminate Modal State
  const [terminateModal, setTerminateModal] = useState({
    open: false,
    doc: null
  });
  const [terminateForm, setTerminateForm] = useState({
    terminatedAt: new Date().toISOString().substring(0, 10),
    reasonCategory: 'Mutual Agreement',
    notes: '',
    sendEmail: false, // Flag [Yes | No]
    ccEmails: ''
  });
  const [terminating, setTerminating] = useState(false);

  // SOW Expiry Alert Config Modal State
  const [alertConfigModal, setAlertConfigModal] = useState({
    open: false
  });
  const [alertConfigForm, setAlertConfigForm] = useState({
    isEnabled: true,
    alertDaysList: '60,30,15,7',
    primaryRecipients: '',
    ccRecipients: '',
    notifyCounterparty: true
  });
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [triggeringWatchdog, setTriggeringWatchdog] = useState(false);
  const [watchdogResult, setWatchdogResult] = useState(null);

  const handleOpenTerminateModal = (doc) => {
    setTerminateModal({
      open: true,
      doc
    });
    setTerminateForm({
      terminatedAt: new Date().toISOString().substring(0, 10),
      reasonCategory: 'Mutual Agreement',
      notes: '',
      sendEmail: false,
      ccEmails: ''
    });
  };

  const handleConfirmTerminate = async () => {
    if (!terminateModal.doc) return;
    try {
      setTerminating(true);
      const token = localStorage.getItem('token');
      const combinedReason = terminateForm.notes?.trim()
        ? `${terminateForm.reasonCategory}: ${terminateForm.notes.trim()}`
        : terminateForm.reasonCategory;

      const payload = {
        reason: combinedReason,
        terminatedAtUtc: terminateForm.terminatedAt,
        sendEmail: Boolean(terminateForm.sendEmail),
        notes: terminateForm.notes,
        ccEmails: terminateForm.ccEmails
      };

      const res = await axios.post(
        `${BASE_URL}/api/crm/commercial-documents/${terminateModal.doc.CommercialDocumentId}/terminate`,
        payload,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (res.data?.success) {
        Swal.fire({
          icon: 'success',
          title: 'SOW Terminated',
          text: `Agreement ${terminateModal.doc.DocumentNumber || ''} has been marked as TERMINATED.${terminateForm.sendEmail ? ' Notification email sent.' : ' (No email sent).'}`
        });
        setTerminateModal({ open: false, doc: null });
        fetchDocuments();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Termination Failed',
          text: res.data?.message || 'Could not terminate SOW.'
        });
      }
    } catch (err) {
      console.error('Error terminating SOW:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Error terminating SOW.'
      });
    } finally {
      setTerminating(false);
    }
  };

  const handleOpenAlertConfigModal = async () => {
    setAlertConfigModal({ open: true });
    setWatchdogResult(null);
    setLoadingConfig(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/crm/commercial-documents/config/sow-expiry-alert`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.data?.success && res.data.data) {
        const cfg = res.data.data;
        const rawDays = cfg.alertDaysList !== undefined ? cfg.alertDaysList : cfg.AlertDaysList;
        const daysStr = Array.isArray(rawDays) ? rawDays.join(',') : (rawDays || '60,30,15,7');
        const prim = cfg.primaryRecipients ?? cfg.PrimaryRecipients ?? '';
        const cc = cfg.ccRecipients ?? cfg.CcRecipients ?? '';
        const isEn = cfg.isEnabled !== undefined ? Boolean(cfg.isEnabled) : (cfg.IsEnabled !== undefined ? Boolean(cfg.IsEnabled) : true);
        const notCp = cfg.notifyCounterparty !== undefined ? Boolean(cfg.notifyCounterparty) : (cfg.NotifyCounterparty !== undefined ? Boolean(cfg.NotifyCounterparty) : true);

        setAlertConfigForm({
          isEnabled: isEn,
          alertDaysList: daysStr,
          primaryRecipients: prim,
          ccRecipients: cc,
          notifyCounterparty: notCp
        });
      }
    } catch (err) {
      console.error('Error fetching alert config:', err);
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleSaveAlertConfig = async () => {
    try {
      setSavingConfig(true);
      const token = localStorage.getItem('token');
      const payload = {
        configKey: 'SOW_EXPIRY_CONFIG',
        isEnabled: Boolean(alertConfigForm.isEnabled),
        alertDaysList: alertConfigForm.alertDaysList,
        primaryRecipients: alertConfigForm.primaryRecipients,
        ccRecipients: alertConfigForm.ccRecipients,
        notifyCounterparty: Boolean(alertConfigForm.notifyCounterparty)
      };
      const res = await axios.put(`${BASE_URL}/api/crm/commercial-documents/config/sow-expiry-alert`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.data?.success) {
        Swal.fire({
          icon: 'success',
          title: 'Configuration Saved',
          text: 'SOW expiry alert reminder settings have been updated.',
          timer: 1800,
          showConfirmButton: false
        });
        setAlertConfigModal({ open: false });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Save Failed',
          text: res.data?.message || 'Could not update alert settings.'
        });
      }
    } catch (err) {
      console.error('Error saving alert config:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Error updating alert settings.'
      });
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTriggerWatchdogCheck = async () => {
    try {
      setTriggeringWatchdog(true);
      setWatchdogResult(null);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${BASE_URL}/api/crm/commercial-documents/watchdog/trigger-sow-check`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.data?.success) {
        setWatchdogResult(res.data.data || { totalEvaluated: 0, remindersSent: 0, expiredSent: 0 });
        Swal.fire({
          icon: 'success',
          title: 'Watchdog Check Executed',
          text: `Scan complete: ${res.data.data?.totalEvaluated || 0} SOWs evaluated, ${res.data.data?.remindersSent || 0} reminders sent, ${res.data.data?.expiredSent || 0} marked expired.`,
          timer: 2500,
          showConfirmButton: false
        });
        fetchDocuments();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Execution Failed',
          text: res.data?.message || 'Could not execute watchdog check.'
        });
      }
    } catch (err) {
      console.error('Error triggering watchdog:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message
      });
    } finally {
      setTriggeringWatchdog(false);
    }
  };

  const handleToggleMilestoneDay = (day) => {
    const dayStr = String(day);
    const current = (alertConfigForm.alertDaysList || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    let next;
    if (current.includes(dayStr)) {
      next = current.filter(d => d !== dayStr);
    } else {
      next = [...current, dayStr];
    }
    next.sort((a, b) => Number(b) - Number(a));
    setAlertConfigForm(prev => ({
      ...prev,
      alertDaysList: next.join(',')
    }));
  };

  // Fetch Documents for the active entity
  const fetchDocuments = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.get(`${BASE_URL}/api/crm/commercial-documents`, {
        params: {
          governingEntityId: currentEntity.id,
          pageSize: 100
        },
        headers
      });

      if (res.data?.success && Array.isArray(res.data.data)) {
        setDocuments(res.data.data);
      } else {
        setDocuments([]);
      }
    } catch (err) {
      console.error('Error fetching commercial documents:', err);
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  }, [currentEntity.id]);

  const handleDeleteDoc = async (doc) => {
    const docIdentifier = doc.DocumentNumber || doc.Title || `#${doc.CommercialDocumentId}`;
    const result = await Swal.fire({
      title: 'Delete Document?',
      text: `Are you sure you want to delete ${docIdentifier}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it'
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${BASE_URL}/api/crm/commercial-documents/${doc.CommercialDocumentId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: `Agreement ${docIdentifier} has been deleted.`,
          timer: 1500,
          showConfirmButton: false
        });
        fetchDocuments();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: res.data.message || 'Could not delete agreement.'
        });
      }
    } catch (err) {
      console.error('Error deleting commercial document:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Error deleting agreement.'
      });
    }
  };

  // Fetch Templates
  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.get(`${BASE_URL}/api/crm/commercial-documents/templates`, { 
        params: { governingEntityId: currentEntity.id },
        headers 
      });
      if (res.data?.success && Array.isArray(res.data.data)) {
        setTemplates(res.data.data);
      } else {
        setTemplates([]);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [currentEntity.id]);

  useEffect(() => {
    fetchDocuments();
    fetchTemplates();
  }, [fetchDocuments, fetchTemplates]);

  // Status Badge Helper matching 2nd image
  const getStatusBadge = (status) => {
    const s = String(status || '').toUpperCase();
    switch (s) {
      case 'DRAFT':
        return { bg: '#f1f5f9', color: '#475569', label: 'Draft' };
      case 'INTERNAL_REVIEW':
      case 'PENDING_REVIEW':
        return { bg: '#fef3c7', color: '#92400e', label: 'In Review' };
      case 'SENT_FOR_SIGNATURE':
        return { bg: '#e0f2fe', color: '#0369a1', label: 'Sent for Sign' };
      case 'PARTIALLY_SIGNED':
        return { bg: '#f3e8ff', color: '#6b21a8', label: 'Partially Signed' };
      case 'EXECUTED':
      case 'ACTIVE':
        return { bg: '#dcfce7', color: '#166534', label: 'Executed' };
      case 'REJECTED':
      case 'CANCELLED':
        return { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' };
      case 'EXPIRED':
        return { bg: '#fee2e2', color: '#b91c1c', label: 'Expired' };
      case 'TERMINATED':
        return { bg: '#ffe4e6', color: '#be123c', label: 'Terminated' };
      default:
        return { bg: '#f1f5f9', color: '#64748b', label: status || 'Draft' };
    }
  };

  // Doc Type badge style
  const getDocTypeBadgeStyle = (code) => {
    const c = String(code || '').toUpperCase();
    switch (c) {
      case 'MSA':
        return { background: '#e0e7ff', color: '#3730a3' }; // Indigo/Purple
      case 'PO':
        return { background: '#e0f2fe', color: '#0369a1' }; // Sky Blue
      case 'SOW':
        return { background: '#dbeafe', color: '#1e40af' }; // Blue
      default:
        return { background: '#f3f4f6', color: '#374151' };
    }
  };

  // Filtered lists based on active tab
  const msaDocs = useMemo(() => {
    return documents.filter(d => d.DocumentTypeCode === 'MSA');
  }, [documents]);

  const poDocs = useMemo(() => {
    return documents.filter(d => d.DocumentTypeCode === 'PO');
  }, [documents]);

  const sowDocs = useMemo(() => {
    return documents.filter(d => d.DocumentTypeCode === 'SOW');
  }, [documents]);

  // Filtered documents currently displayed
  const currentTabDocs = useMemo(() => {
    const baseList = activeTab === 'MSA' ? msaDocs : (activeTab === 'PO' ? poDocs : (activeTab === 'SOW' ? sowDocs : []));
    return baseList.filter(doc => {
      // Search filter
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const num = (doc.DocumentNumber || '').toLowerCase();
        const title = (doc.Title || '').toLowerCase();
        const cpName = (doc.CounterpartyName || doc.CounterpartyLegalName || '').toLowerCase();
        if (!num.includes(q) && !title.includes(q) && !cpName.includes(q)) {
          return false;
        }
      }

      // Type filter
      if (typeFilter !== 'ALL' && doc.DocumentTypeCode !== typeFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== 'ALL') {
        const docStatus = (doc.StatusCode || '').toUpperCase();
        if (statusFilter === 'ACTIVE') {
          if (docStatus !== 'ACTIVE' && docStatus !== 'EXECUTED') return false;
        } else if (statusFilter === 'DRAFT') {
          if (docStatus !== 'DRAFT') return false;
        } else if (statusFilter === 'PARTIALLY_SIGNED') {
          if (docStatus !== 'PARTIALLY_SIGNED') return false;
        } else if (statusFilter === 'SENT_FOR_SIGNATURE') {
          if (docStatus !== 'SENT_FOR_SIGNATURE') return false;
        } else if (statusFilter === 'EXECUTED') {
          if (docStatus !== 'EXECUTED' && docStatus !== 'ACTIVE') return false;
        } else if (statusFilter === 'CANCELLED') {
          if (docStatus !== 'CANCELLED' && docStatus !== 'REJECTED') return false;
        } else if (statusFilter === 'EXPIRED') {
          if (docStatus !== 'EXPIRED') return false;
        } else if (statusFilter === 'TERMINATED') {
          if (docStatus !== 'TERMINATED') return false;
        }
      }

      // Side filter
      if (sideFilter !== 'ALL') {
        if ((doc.CounterpartySide || '').toUpperCase() !== sideFilter.toUpperCase()) {
          return false;
        }
      }

      return true;
    });
  }, [activeTab, msaDocs, poDocs, sowDocs, searchTerm, typeFilter, statusFilter, sideFilter]);

  return (
    <div className="msa-po-container">
      {/* Breadcrumbs */}
      <div className="msa-po-breadcrumbs">
        <span className="clickable" onClick={() => navigate('/crm/companies')}>CRM</span>
        <span>/</span>
        <span className="clickable" onClick={() => navigate('/crm/msa-po')}>MSA & PO</span>
        <span>/</span>
        <span className="current">{currentEntity.name}</span>
      </div>

      {/* Header Row */}
      <div className="msa-po-header">
        <div className="msa-po-title-area">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1>
              <LuFileText size={24} color={currentEntity.accentColor} />
              {currentEntity.name}
            </h1>
            <span className="msa-pill-badge" style={{ background: '#f1f5f9', color: '#475569' }}>
              Entity #{currentEntity.code}
            </span>

            {/* Quick Entity Switcher Dropdown */}
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <select
                className="msa-select"
                value={String(currentEntity.id)}
                onChange={(e) => navigate(`/crm/msa-po/${e.target.value}`)}
                style={{ fontSize: '12px', padding: '4px 10px', height: '30px' }}
                title="Switch Entity Workspace"
              >
                {entitiesList.length > 0 ? (
                  entitiesList.map(ent => (
                    <option key={ent.Id || ent.id} value={String(ent.Id || ent.id)}>
                      {ent.Name || ent.name}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="6">Cognifyar Technologies</option>
                    <option value="5">ProphecyTek Pvt Ltd</option>
                    <option value="4">Prophecy Consulting Inc</option>
                  </>
                )}
              </select>
            </div>
          </div>
          <p>Manage Master Service Agreements, Purchase Orders, and legal contract templates for {currentEntity.fullName}.</p>
        </div>

        {/* Action Buttons */}
        <div className="msa-po-actions">
          {activeTab === 'TEMPLATES' ? (
            <button
              type="button"
              className="msa-btn-primary"
              style={{ backgroundColor: currentEntity.accentColor }}
              onClick={() => handleOpenTemplateModal(null, 'CREATE')}
            >
              <LuPlus size={15} /> Create New Template
            </button>
          ) : (
            <>
              <button
                type="button"
                className="msa-btn-outline"
                onClick={() => setCommModal({
                  open: true,
                  mode: 'IMPORT',
                  documentId: null,
                  documentTypeCode: activeTab === 'PO' ? 'PO' : (activeTab === 'SOW' ? 'SOW' : 'MSA')
                })}
              >
                <LuUpload size={14} /> Import Executed
              </button>

              <button
                type="button"
                className="msa-btn-primary"
                style={{ backgroundColor: currentEntity.accentColor }}
                onClick={() => setCommModal({
                  open: true,
                  mode: 'CREATE_DRAFT',
                  documentId: null,
                  documentTypeCode: activeTab === 'PO' ? 'PO' : (activeTab === 'SOW' ? 'SOW' : 'MSA')
                })}
              >
                <LuPlus size={15} /> Draft New Agreement
              </button>
            </>
          )}
        </div>
      </div>

      {/* 4 Tabs Bar: MSA | PO | SOW | Templates */}
      <div className="msa-po-tabs-bar">
        <button
          type="button"
          className={`msa-po-tab-btn ${activeTab === 'MSA' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('MSA');
            setTypeFilter('ALL');
          }}
        >
          <span>MSA</span>
          <span className="msa-po-tab-count">{msaDocs.length}</span>
        </button>

        <button
          type="button"
          className={`msa-po-tab-btn ${activeTab === 'PO' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('PO');
            setTypeFilter('ALL');
          }}
        >
          <span>PO</span>
          <span className="msa-po-tab-count">{poDocs.length}</span>
        </button>

        <button
          type="button"
          className={`msa-po-tab-btn ${activeTab === 'SOW' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('SOW');
            setTypeFilter('ALL');
          }}
        >
          <span>SOW</span>
          <span className="msa-po-tab-count">{sowDocs.length}</span>
        </button>

        <button
          type="button"
          className={`msa-po-tab-btn ${activeTab === 'TEMPLATES' ? 'active' : ''}`}
          onClick={() => setActiveTab('TEMPLATES')}
        >
          <span>Templates</span>
          <span className="msa-po-tab-count">{templates.length}</span>
        </button>
      </div>

      {/* TAB 1: MSA, TAB 2: PO, TAB 3: SOW */}
      {(activeTab === 'MSA' || activeTab === 'PO' || activeTab === 'SOW') && (
        <div>
          {/* Filter Toolbar Matching Image 2 */}
          <div className="msa-po-toolbar">
            <div className="msa-po-toolbar-left">
              {/* Search Box */}
              <div className="msa-search-wrapper">
                <LuSearch size={15} className="msa-search-icon" />
                <input
                  type="text"
                  placeholder="Search by Document # or Title..."
                  className="msa-search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Types Dropdown */}
              <select
                className="msa-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                style={{ width: '130px' }}
              >
                <option value="ALL">All Types</option>
                {activeTab === 'MSA' && <option value="MSA">MSA</option>}
                {activeTab === 'PO' && <option value="PO">PO</option>}
                {activeTab === 'SOW' && <option value="SOW">SOW</option>}
              </select>

              {/* Statuses Dropdown */}
              <select
                className="msa-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ width: '150px' }}
              >
                <option value="ALL">All Statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="DRAFT">Draft</option>
                <option value="PARTIALLY_SIGNED">Partially Signed</option>
                <option value="SENT_FOR_SIGNATURE">Sent for Sign</option>
                <option value="EXECUTED">Executed</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="EXPIRED">Expired</option>
                <option value="TERMINATED">Terminated</option>
              </select>

              {/* Side Filter */}
              <select
                className="msa-select"
                value={sideFilter}
                onChange={(e) => setSideFilter(e.target.value)}
                style={{ width: '130px' }}
              >
                <option value="ALL">All Sides</option>
                <option value="SUPPLIER">Supplier</option>
                <option value="CLIENT">Client</option>
              </select>
            </div>

            {/* SOW-specific Expiry Alert Settings action button */}
            {activeTab === 'SOW' && (
              <div className="msa-po-toolbar-right" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  type="button"
                  className="msa-btn-outline"
                  onClick={handleOpenAlertConfigModal}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    padding: '7px 14px',
                    borderColor: '#bae6fd',
                    color: '#0369a1',
                    background: '#f0f9ff'
                  }}
                  title="Configure automated email reminders before SOW expiration"
                >
                  <LuBellRing size={15} color="#0284c7" />
                  <span>Expiry Alert Settings</span>
                </button>
              </div>
            )}
          </div>

          {/* Table Matching Image 2 Layout */}
          <div className="msa-table-card">
            <table className="msa-table">
              <thead>
                <tr>
                  <th>DOCUMENT #</th>
                  <th>TYPE</th>
                  <th>TITLE</th>
                  <th>SIDE</th>
                  <th>STATUS</th>
                  <th>START DATE</th>
                  <th>END DATE</th>
                  <th>MODIFIED DATE</th>
                  <th style={{ textAlign: 'right' }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {currentTabDocs.map((doc) => {
                  const statusBadge = getStatusBadge(doc.StatusCode);
                  const startDateStr = doc.EffectiveFromDate 
                    ? new Date(doc.EffectiveFromDate).toLocaleDateString() 
                    : (doc.CreatedAtUtc ? new Date(doc.CreatedAtUtc).toLocaleDateString() : '—');
                  const endDateStr = doc.EffectiveToDate 
                    ? new Date(doc.EffectiveToDate).toLocaleDateString() 
                    : '—';
                  const modifiedDateStr = doc.UpdatedAtUtc 
                    ? new Date(doc.UpdatedAtUtc).toLocaleDateString() 
                    : (doc.CreatedAtUtc ? new Date(doc.CreatedAtUtc).toLocaleDateString() : '—');

                  return (
                    <tr key={doc.CommercialDocumentId}>
                      {/* 1. DOCUMENT # */}
                      <td>
                        <span 
                          className="msa-doc-num-link"
                          onClick={() => setCommModal({
                            open: true,
                            mode: 'VIEW_AND_SIGN',
                            documentId: doc.CommercialDocumentId,
                            documentTypeCode: doc.DocumentTypeCode
                          })}
                        >
                          {doc.DocumentNumber || `#${doc.CommercialDocumentId}`}
                        </span>
                      </td>

                      {/* 2. TYPE */}
                      <td>
                        <span 
                          className="msa-pill-badge" 
                          style={getDocTypeBadgeStyle(doc.DocumentTypeCode)}
                        >
                          {doc.DocumentTypeCode}
                        </span>
                      </td>

                      {/* 3. TITLE */}
                      <td>
                        <div style={{ fontWeight: 500, color: '#1e293b' }}>
                          {doc.Title || `${doc.DocumentTypeCode} – ${doc.CounterpartyName || doc.CounterpartyLegalName || 'Company'}`}
                        </div>
                      </td>

                      {/* 4. SIDE */}
                      <td>
                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                          {doc.CounterpartySide || 'SUPPLIER'}
                        </span>
                      </td>

                      {/* 5. STATUS */}
                      <td>
                        <span 
                          className="msa-status-chip"
                          style={{
                            backgroundColor: statusBadge.bg,
                            color: statusBadge.color
                          }}
                        >
                          {statusBadge.label}
                        </span>
                      </td>

                      {/* 6. START DATE */}
                      <td>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>
                          {startDateStr}
                        </span>
                      </td>

                      {/* 7. END DATE */}
                      <td>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>
                          {endDateStr}
                        </span>
                      </td>

                      {/* 8. MODIFIED DATE */}
                      <td>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>
                          {modifiedDateStr}
                        </span>
                      </td>

                      {/* 9. ACTIONS */}
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                          <button
                            type="button"
                            className="msa-view-sign-btn"
                            onClick={() => setCommModal({
                              open: true,
                              mode: 'VIEW_AND_SIGN',
                              documentId: doc.CommercialDocumentId,
                              documentTypeCode: doc.DocumentTypeCode,
                              autoEdit: false
                            })}
                            title="Open document for review, tracking and signing"
                          >
                            <LuEye size={13} />
                            <span>View & Sign</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setCommModal({
                              open: true,
                              mode: 'EDIT',
                              documentId: doc.CommercialDocumentId,
                              documentTypeCode: doc.DocumentTypeCode,
                              autoEdit: true
                            })}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              background: '#f0f9ff',
                              color: '#0369a1',
                              border: '1px solid #bae6fd',
                              padding: '5px 9px',
                              borderRadius: '4px',
                              fontSize: '11.5px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                            title="Edit Agreement Details & Terms"
                          >
                            <LuPencil size={13} />
                            <span>Edit</span>
                          </button>
                          {doc.DocumentTypeCode === 'SOW' && !['TERMINATED', 'VOID', 'CANCELLED'].includes((doc.StatusCode || '').toUpperCase()) && (
                            <button
                              type="button"
                              onClick={() => handleOpenTerminateModal(doc)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: '#fff1f2',
                                color: '#e11d48',
                                border: '1px solid #fecdd3',
                                padding: '5px 9px',
                                borderRadius: '4px',
                                fontSize: '11.5px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                              title="Terminate Statement of Work"
                            >
                              <LuBan size={13} />
                              <span>Terminate</span>
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDeleteDoc(doc)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              background: '#fef2f2',
                              color: '#b91c1c',
                              border: '1px solid #fecaca',
                              padding: '5px 9px',
                              borderRadius: '4px',
                              fontSize: '11.5px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                            title="Delete Agreement"
                          >
                            <LuTrash2 size={13} />
                            <span>Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {/* Empty State */}
                {currentTabDocs.length === 0 && !loadingDocs && (
                  <tr>
                    <td colSpan="9">
                      <div className="msa-empty-state">
                        <div className="msa-empty-state-icon">
                          <LuFileCheck size={36} />
                        </div>
                        <h3>No {activeTab} agreements found</h3>
                        <p>There are no {activeTab} documents matching your selected filters for {currentEntity.name}.</p>
                        <button
                          type="button"
                          className="msa-btn-primary"
                          style={{ backgroundColor: currentEntity.accentColor }}
                          onClick={() => setCommModal({
                            open: true,
                            mode: 'CREATE_DRAFT',
                            documentId: null,
                            documentTypeCode: activeTab === 'PO' ? 'PO' : (activeTab === 'SOW' ? 'SOW' : 'MSA')
                          })}
                        >
                          <LuPlus size={14} /> Draft {activeTab} Document
                        </button>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Loading State */}
                {loadingDocs && (
                  <tr>
                    <td colSpan="9" style={{ padding: '36px', textAlign: 'center', color: '#94a3b8' }}>
                      Loading {activeTab} agreements...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: TEMPLATES */}
      {activeTab === 'TEMPLATES' && (
        <div className="msa-table-card">
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff' }}>
            <div>
              <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#0f172a' }}>
                Document Templates Library
              </h4>
              <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#64748b' }}>
                Master legal contracts and purchase order templates configured for {currentEntity.fullName}.
              </p>
            </div>
            <button
              type="button"
              className="msa-btn-primary"
              style={{ backgroundColor: currentEntity.accentColor, padding: '7px 14px', fontSize: '13px' }}
              onClick={() => handleOpenTemplateModal(null, 'CREATE')}
            >
              <LuPlus size={14} /> Create New Template
            </button>
          </div>
          <table className="msa-table">
            <thead>
              <tr>
                <th>TEMPLATE NAME</th>
                <th>TYPE</th>
                <th>APPLICABLE SIDE</th>
                <th>VERSION</th>
                <th>DEFAULT FOR ENTITY</th>
                <th style={{ textAlign: 'right' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((tmpl) => (
                <tr key={tmpl.CommercialTemplateId}>
                  <td>
                    <div 
                      style={{ 
                        fontWeight: 600, 
                        color: '#0f172a',
                        maxWidth: '240px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                      title={tmpl.TemplateName}
                    >
                      {tmpl.TemplateName}
                    </div>
                    {tmpl.Description && (
                      <div 
                        style={{ 
                          fontSize: '11px', 
                          color: '#64748b', 
                          marginTop: '2px',
                          maxWidth: '240px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        title={tmpl.Description}
                      >
                        {tmpl.Description}
                      </div>
                    )}
                  </td>
                  <td>
                    <span 
                      className="msa-pill-badge" 
                      style={getDocTypeBadgeStyle(tmpl.DocumentTypeCode)}
                    >
                      {tmpl.DocumentTypeCode}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                      {tmpl.CounterpartySide || 'Both (Supplier & Client)'}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>
                      v{tmpl.CurrentVersionNo || '1.0'}
                    </span>
                  </td>
                  <td>
                    {tmpl.IsDefault ? (
                      <span className="msa-pill-badge" style={{ background: '#dcfce7', color: '#166534' }}>
                        Default Template ★
                      </span>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#94a3b8' }}>Standard</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="msa-btn-outline"
                        style={{ padding: '5px 10px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#0369a1', borderColor: '#bae6fd' }}
                        title="View Template Details & Versions"
                        onClick={() => handleOpenTemplateModal(tmpl, 'VIEW')}
                      >
                        <LuEye size={13} />
                        <span>View</span>
                      </button>
                      <button
                        type="button"
                        className="msa-btn-outline"
                        style={{ padding: '5px 10px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#4338ca', borderColor: '#c7d2fe' }}
                        title="Edit Template Details & Upload Version"
                        onClick={() => handleOpenTemplateModal(tmpl, 'EDIT')}
                      >
                        <LuPencil size={13} />
                        <span>Edit</span>
                      </button>
                      <a
                        href={`${BASE_URL}/api/crm/commercial-documents/templates/${tmpl.CommercialTemplateId}/download`}
                        download
                        className="msa-btn-outline"
                        style={{ padding: '6px 8px', fontSize: '12px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#0284c7', borderColor: '#bae6fd', borderRadius: '5px' }}
                        title="Download Template (.docx)"
                      >
                        <LuDownload size={14} />
                      </a>
                      <button
                        type="button"
                        className="msa-btn-outline"
                        style={{ padding: '6px 8px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626', borderColor: '#fecaca', borderRadius: '5px' }}
                        title={`Delete Template "${tmpl.TemplateName}"`}
                        onClick={() => handleDeleteTemplate(tmpl)}
                      >
                        <LuTrash2 size={14} />
                      </button>
                      <button
                        type="button"
                        className="msa-btn-primary"
                        style={{ padding: '5px 12px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                        onClick={() => setCommModal({
                          open: true,
                          mode: 'CREATE_DRAFT',
                          documentId: null,
                          documentTypeCode: tmpl.DocumentTypeCode || 'MSA'
                        })}
                      >
                        <LuFilePlus size={13} />
                        <span>Use Template</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {templates.length === 0 && !loadingTemplates && (
                <tr>
                  <td colSpan="6">
                    <div className="msa-empty-state">
                      <LuLayers size={36} className="msa-empty-state-icon" />
                      <h3>No Templates Found</h3>
                      <p>Standard master contract templates can be registered via the system configuration.</p>
                    </div>
                  </td>
                </tr>
              )}

              {loadingTemplates && (
                <tr>
                  <td colSpan="6" style={{ padding: '36px', textAlign: 'center', color: '#94a3b8' }}>
                    Loading templates library...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Commercial Document Modal for Review, View & Sign, Drafting, & Import */}
      {commModal.open && (
        <CommercialDocumentModal
          isOpen={commModal.open}
          onClose={() => {
            setCommModal({ open: false, mode: 'VIEW_AND_SIGN', documentId: null, documentTypeCode: 'MSA' });
            fetchDocuments();
          }}
          initialMode={commModal.mode}
          autoEdit={Boolean(commModal.autoEdit)}
          documentId={commModal.documentId}
          documentTypeCode={commModal.documentTypeCode}
          governingEntityId={currentEntity.id}
          governingEntityName={currentEntity.fullName}
          onCreatedSuccess={() => {
            fetchDocuments();
          }}
          onLinkedSuccess={() => {
            fetchDocuments();
          }}
        />
      )}

      {/* Template View & Edit Modal */}
      {templateModal.open && (
        <TemplateModal
          isOpen={templateModal.open}
          onClose={() => setTemplateModal({ open: false, template: null, mode: 'VIEW' })}
          template={templateModal.template}
          initialMode={templateModal.mode}
          governingEntityId={currentEntity.id}
          governingEntityName={currentEntity.fullName}
          onSuccess={() => fetchTemplates()}
          onUseTemplate={(tmpl) => {
            setCommModal({
              open: true,
              mode: 'CREATE_DRAFT',
              documentId: null,
              documentTypeCode: tmpl?.DocumentTypeCode || 'MSA'
            });
          }}
        />
      )}

      {/* SOW Terminate Modal */}
      {terminateModal.open && terminateModal.doc && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '16px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '560px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              background: 'linear-gradient(135deg, #991b1b 0%, #dc2626 100%)',
              color: '#ffffff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ background: 'rgba(255, 255, 255, 0.2)', padding: '8px', borderRadius: '8px', display: 'flex' }}>
                  <LuBan size={20} color="#ffffff" />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#ffffff' }}>
                    Terminate Statement of Work (SOW)
                  </h3>
                  <div style={{ fontSize: '0.78rem', color: '#fee2e2', marginTop: '2px' }}>
                    Document: <strong>{terminateModal.doc.DocumentNumber || `#${terminateModal.doc.CommercialDocumentId}`}</strong>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setTerminateModal({ open: false, doc: null })}
                style={{ background: 'transparent', border: 'none', color: '#fee2e2', cursor: 'pointer', padding: '4px' }}
              >
                <LuX size={20} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '78vh', overflowY: 'auto' }}>
              {/* Document Overview banner */}
              <div style={{ background: '#f8fafc', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12.5px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ color: '#64748b' }}>Title:</span>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{terminateModal.doc.Title || 'Statement of Work'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ color: '#64748b' }}>Counterparty:</span>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{terminateModal.doc.CounterpartyName || terminateModal.doc.CounterpartyLegalName || '—'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Effective Term:</span>
                  <span style={{ color: '#334155' }}>
                    {terminateModal.doc.EffectiveFromDate ? new Date(terminateModal.doc.EffectiveFromDate).toLocaleDateString() : '—'} &nbsp;→&nbsp; {terminateModal.doc.EffectiveToDate ? new Date(terminateModal.doc.EffectiveToDate).toLocaleDateString() : 'Indefinite'}
                  </span>
                </div>
              </div>

              {/* Termination Date */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Effective Termination Date <span style={{ color: '#e11d48' }}>*</span>
                </label>
                <input
                  type="date"
                  className="msa-select"
                  style={{ width: '100%', boxSizing: 'border-box' }}
                  value={terminateForm.terminatedAt}
                  onChange={(e) => setTerminateForm(prev => ({ ...prev, terminatedAt: e.target.value }))}
                />
              </div>

              {/* Reason Category */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Termination Reason Category <span style={{ color: '#e11d48' }}>*</span>
                </label>
                <select
                  className="msa-select"
                  style={{ width: '100%', boxSizing: 'border-box' }}
                  value={terminateForm.reasonCategory}
                  onChange={(e) => setTerminateForm(prev => ({ ...prev, reasonCategory: e.target.value }))}
                >
                  <option value="Mutual Agreement">Mutual Agreement between Parties</option>
                  <option value="Convenience (Notice Period)">Termination for Convenience (Written Notice)</option>
                  <option value="Project Completion / Early Wrap">Project Milestone Completed / Early Wrap</option>
                  <option value="Budgetary / Client Cancellation">Budgetary / Client Cancellation</option>
                  <option value="Material Breach">Material Breach of Agreement Terms</option>
                  <option value="Non-Performance / Quality Issue">Non-Performance / Scope Dispute</option>
                  <option value="Other">Other (Specified Below)</option>
                </select>
              </div>

              {/* Notes / Details */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Detailed Notes / Reference Info
                </label>
                <textarea
                  className="msa-select"
                  rows={3}
                  style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: '65px' }}
                  placeholder="Specify written notice date, deliverable handover details, or reasons..."
                  value={terminateForm.notes}
                  onChange={(e) => setTerminateForm(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>

              {/* Send Email [Yes | No] Flag */}
              <div style={{
                background: terminateForm.sendEmail ? '#f0fdf4' : '#f8fafc',
                border: terminateForm.sendEmail ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '14px',
                transition: 'all 0.2s ease'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <LuMail size={15} color={terminateForm.sendEmail ? '#166534' : '#64748b'} />
                      <span>Send Email Notification:</span>
                    </div>
                    <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>
                      Notify counterparty and stakeholder list regarding SOW termination
                    </div>
                  </div>

                  {/* Yes | No Toggle Switch / Buttons */}
                  <div style={{ display: 'inline-flex', background: '#e2e8f0', padding: '3px', borderRadius: '20px' }}>
                    <button
                      type="button"
                      onClick={() => setTerminateForm(prev => ({ ...prev, sendEmail: true }))}
                      style={{
                        border: 'none',
                        background: terminateForm.sendEmail ? '#16a34a' : 'transparent',
                        color: terminateForm.sendEmail ? '#ffffff' : '#475569',
                        padding: '4px 14px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <LuCheck size={12} /> Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setTerminateForm(prev => ({ ...prev, sendEmail: false }))}
                      style={{
                        border: 'none',
                        background: !terminateForm.sendEmail ? '#64748b' : 'transparent',
                        color: !terminateForm.sendEmail ? '#ffffff' : '#475569',
                        padding: '4px 14px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <LuX size={12} /> No
                    </button>
                  </div>
                </div>

                {terminateForm.sendEmail ? (
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #86efac' }}>
                    <div style={{ fontSize: '11.5px', color: '#15803d', marginBottom: '8px', fontWeight: 600 }}>
                      ✓ An official Termination Notice email will be dispatched to the counterparty signing contacts.
                    </div>
                    <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                      Additional CC Recipients (Optional comma-separated):
                    </label>
                    <input
                      type="text"
                      className="msa-select"
                      style={{ width: '100%', boxSizing: 'border-box', fontSize: '12px', padding: '6px 10px' }}
                      placeholder="e.g. legal@prophecytech.com, accountmgr@client.com"
                      value={terminateForm.ccEmails}
                      onChange={(e) => setTerminateForm(prev => ({ ...prev, ccEmails: e.target.value }))}
                    />
                  </div>
                ) : (
                  <div style={{ fontSize: '11.5px', color: '#64748b', fontStyle: 'italic' }}>
                    Status will be updated to Terminated in CRM without sending external emails.
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '14px 20px',
              background: '#f8fafc',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px'
            }}>
              <button
                type="button"
                className="msa-btn-outline"
                onClick={() => setTerminateModal({ open: false, doc: null })}
                disabled={terminating}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmTerminate}
                disabled={terminating}
                style={{
                  background: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 18px',
                  fontSize: '13px',
                  fontWeight: 650,
                  cursor: terminating ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 1px 2px rgba(220, 38, 38, 0.2)'
                }}
              >
                <LuBan size={14} />
                <span>{terminating ? 'Terminating...' : 'Confirm Termination'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SOW Expiry Alert Configuration Modal */}
      {alertConfigModal.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '16px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '620px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              background: 'linear-gradient(135deg, #0369a1 0%, #0284c7 100%)',
              color: '#ffffff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ background: 'rgba(255, 255, 255, 0.2)', padding: '8px', borderRadius: '8px', display: 'flex' }}>
                  <LuBellRing size={20} color="#ffffff" />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#ffffff' }}>
                    SOW Expiry Alert Reminder Settings
                  </h3>
                  <div style={{ fontSize: '0.78rem', color: '#e0f2fe', marginTop: '2px' }}>
                    Configure automated notifications before Statements of Work reach their end date
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAlertConfigModal({ open: false })}
                style={{ background: 'transparent', border: 'none', color: '#e0f2fe', cursor: 'pointer', padding: '4px' }}
              >
                <LuX size={20} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '18px', maxHeight: '78vh', overflowY: 'auto' }}>
              {loadingConfig ? (
                <div style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                  Loading alert configuration...
                </div>
              ) : (
                <>
                  {/* Enable / Disable Master Toggle */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    background: alertConfigForm.isEnabled ? '#f0fdf4' : '#f8fafc',
                    borderRadius: '8px',
                    border: alertConfigForm.isEnabled ? '1px solid #bbf7d0' : '1px solid #e2e8f0'
                  }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>
                        Automated Expiry Watchdog
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        Evaluates all active SOWs daily and dispatches milestone reminder emails
                      </div>
                    </div>
                    <div style={{ display: 'inline-flex', background: '#e2e8f0', padding: '3px', borderRadius: '20px' }}>
                      <button
                        type="button"
                        onClick={() => setAlertConfigForm(prev => ({ ...prev, isEnabled: true }))}
                        style={{
                          border: 'none',
                          background: alertConfigForm.isEnabled ? '#16a34a' : 'transparent',
                          color: alertConfigForm.isEnabled ? '#ffffff' : '#475569',
                          padding: '4px 14px',
                          borderRadius: '16px',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        Enabled
                      </button>
                      <button
                        type="button"
                        onClick={() => setAlertConfigForm(prev => ({ ...prev, isEnabled: false }))}
                        style={{
                          border: 'none',
                          background: !alertConfigForm.isEnabled ? '#64748b' : 'transparent',
                          color: !alertConfigForm.isEnabled ? '#ffffff' : '#475569',
                          padding: '4px 14px',
                          borderRadius: '16px',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        Disabled
                      </button>
                    </div>
                  </div>

                  {/* Reminder Milestone Days */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                      Reminder Milestones (Days Before Expiration) <span style={{ color: '#0284c7' }}>*</span>
                    </label>
                    <div style={{ fontSize: '11.5px', color: '#64748b', marginBottom: '8px' }}>
                      Click milestone pills to toggle or type custom days in the box below:
                    </div>
                    {/* Quick Milestone Pills */}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      {[60, 45, 30, 15, 7, 3, 1].map(day => {
                        const isSelected = (alertConfigForm.alertDaysList || '')
                          .split(',')
                          .map(s => s.trim())
                          .includes(String(day));
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => handleToggleMilestoneDay(day)}
                            style={{
                              border: isSelected ? '1px solid #0284c7' : '1px solid #cbd5e1',
                              background: isSelected ? '#e0f2fe' : '#ffffff',
                              color: isSelected ? '#0369a1' : '#475569',
                              padding: '4px 10px',
                              borderRadius: '16px',
                              fontSize: '11.5px',
                              fontWeight: 650,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            {isSelected && <LuCheck size={11} />}
                            {day} Days
                          </button>
                        );
                      })}
                    </div>
                    <input
                      type="text"
                      className="msa-select"
                      style={{ width: '100%', boxSizing: 'border-box' }}
                      placeholder="e.g. 60,30,15,7"
                      value={alertConfigForm.alertDaysList}
                      onChange={(e) => setAlertConfigForm(prev => ({ ...prev, alertDaysList: e.target.value }))}
                    />
                  </div>

                  {/* Primary Alert Recipients */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                      Primary Alert Recipients (Comma-separated) <span style={{ color: '#0284c7' }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="msa-select"
                      style={{ width: '100%', boxSizing: 'border-box' }}
                      placeholder="e.g. contracts@prophecytech.com, legal@prophecytech.com"
                      value={alertConfigForm.primaryRecipients}
                      onChange={(e) => setAlertConfigForm(prev => ({ ...prev, primaryRecipients: e.target.value }))}
                    />
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                      These recipients receive all milestone reminders and final expiration alerts.
                    </div>
                  </div>

                  {/* CC Recipients */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                      CC Alert Recipients (Optional)
                    </label>
                    <input
                      type="text"
                      className="msa-select"
                      style={{ width: '100%', boxSizing: 'border-box' }}
                      placeholder="e.g. operations@prophecytech.com, hr@prophecytech.com"
                      value={alertConfigForm.ccRecipients}
                      onChange={(e) => setAlertConfigForm(prev => ({ ...prev, ccRecipients: e.target.value }))}
                    />
                  </div>

                  {/* Notify Counterparty */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 14px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div>
                      <div style={{ fontSize: '12.5px', fontWeight: 650, color: '#1e293b' }}>
                        Include Counterparty Signatory in Alerts
                      </div>
                      <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                        Also send reminder notice to the counterparty signatory email
                      </div>
                    </div>
                    <div style={{ display: 'inline-flex', background: '#e2e8f0', padding: '2px', borderRadius: '16px' }}>
                      <button
                        type="button"
                        onClick={() => setAlertConfigForm(prev => ({ ...prev, notifyCounterparty: true }))}
                        style={{
                          border: 'none',
                          background: alertConfigForm.notifyCounterparty ? '#0284c7' : 'transparent',
                          color: alertConfigForm.notifyCounterparty ? '#ffffff' : '#475569',
                          padding: '3px 12px',
                          borderRadius: '14px',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        onClick={() => setAlertConfigForm(prev => ({ ...prev, notifyCounterparty: false }))}
                        style={{
                          border: 'none',
                          background: !alertConfigForm.notifyCounterparty ? '#64748b' : 'transparent',
                          color: !alertConfigForm.notifyCounterparty ? '#ffffff' : '#475569',
                          padding: '3px 12px',
                          borderRadius: '14px',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        No
                      </button>
                    </div>
                  </div>

                  {/* Watchdog Manual Test Trigger */}
                  <div style={{
                    background: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontSize: '12.5px', fontWeight: 700, color: '#166534' }}>
                        Manual Watchdog Expiry Scan
                      </div>
                      <div style={{ fontSize: '11px', color: '#4b5563', marginTop: '2px' }}>
                        Test and run an immediate evaluation of all SOW expiration dates
                      </div>
                      {watchdogResult && (
                        <div style={{ fontSize: '11px', color: '#15803d', fontWeight: 600, marginTop: '4px' }}>
                          ✓ Evaluated: {watchdogResult.totalEvaluated} | Reminders sent: {watchdogResult.remindersSent} | Expired: {watchdogResult.expiredSent}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleTriggerWatchdogCheck}
                      disabled={triggeringWatchdog}
                      style={{
                        background: '#16a34a',
                        color: '#ffffff',
                        border: 'none',
                        padding: '6px 14px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 650,
                        cursor: triggeringWatchdog ? 'not-allowed' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <LuPlay size={13} />
                      <span>{triggeringWatchdog ? 'Scanning...' : 'Run Scan Now'}</span>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '14px 20px',
              background: '#f8fafc',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px'
            }}>
              <button
                type="button"
                className="msa-btn-outline"
                onClick={() => setAlertConfigModal({ open: false })}
                disabled={savingConfig}
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleSaveAlertConfig}
                disabled={savingConfig || loadingConfig}
                style={{
                  background: '#0284c7',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 18px',
                  fontSize: '13px',
                  fontWeight: 650,
                  cursor: savingConfig ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 1px 2px rgba(2, 132, 199, 0.2)'
                }}
              >
                <LuCheck size={14} />
                <span>{savingConfig ? 'Saving...' : 'Save Configuration'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MsaPoDashboard;
